<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: PlaceEventsPanel                                      |
| Section: components                                              |
| Purpose: Side panel listing events tied to the selected place.   |
+------------------------------------------------------------------+
-->
<template>
    <div
        :class="['place-events-panel', { 'place-events-panel--visible': visible, 'place-events-panel--inline': inline }]">
        <div class="place-events-panel__overlay" @click="handleClose"></div>

        <!-- Absolute positioned close button (left of panel) -->
        <button v-if="visible" class="place-events-panel__close-external" @click="handleClose"
            :aria-label="$t('placeEventsPanel.close')" :title="$t('placeEventsPanel.closeTitleEsc')">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
            </svg>
        </button>

        <div class="place-events-panel__content">
            <!-- Header -->
            <div class="place-events-panel__header">
                <div class="place-events-panel__title-wrapper">
                    <h2 class="place-events-panel__title">{{ placeName }}</h2>
                    <p class="place-events-panel__subtitle">{{ $t('placeEventsPanel.subtitle') }}</p>

                    <!-- Event counts -->
                    <div v-if="countsLoading || (counts && Object.keys(counts).length > 0)"
                        class="place-events-panel__counts">
                        <div v-if="countsLoading"
                            class="place-events-panel__count-item place-events-panel__count-item--loading"
                            role="status">
                            <span class="place-events-panel__inline-spinner" aria-hidden="true"></span>
                        </div>
                        <div v-for="(count, type) in sortedCounts" :key="type" class="place-events-panel__count-item">
                            <span class="count-label">{{ localizedEventTypeLabels[type] || type }}:</span>
                            <span class="count-value">{{ count }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="place-events-panel__loading">
                <LoadingSpinner />
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="place-events-panel__error">
                <p>{{ error }}</p>
            </div>

            <!-- Events Content -->
            <div v-else-if="!loading && hasPanelContent" class="place-events-panel__body">
                <template v-if="hasEvents && isGroupedPlace">
                    <div class="place-events-panel__event-group" v-for="section in groupedEventsByPlace"
                        :key="section.key">
                        <h3 class="place-events-panel__event-type">{{ section.placeName }}</h3>
                        <div class="place-events-panel__person-cards">
                            <PersonCard v-for="person in section.events" :key="getGroupedPlaceEventKey(person)"
                                :photoSrc="person.photoSrc" :name="person.firstname + ' ' + person.lastname"
                                :years="formatEventDescription(person.eventType, person.eventDate, person.eventYear)"
                                :gender="person.sex" :href="localePath(`/person/${person.personID}`)"
                                :personID="person.personID" :firstname="person.firstname" :lastname="person.lastname"
                                :living="person.living" :placeholder="person.placeholder" :isSmall="true"
                                :class="{ 'PersonCard_small_guest': isPrivatePerson(person) }" />
                        </div>
                    </div>
                </template>

                <template v-else-if="hasEvents">
                    <div class="place-events-panel__event-group" v-for="(members, eventType) in groupedEvents"
                        :key="eventType">
                        <h3 class="place-events-panel__event-type">{{ localizedEventTypeLabels[eventType] || eventType
                        }}</h3>
                        <div class="place-events-panel__person-cards">
                            <PersonCard v-for="person in members" :key="person.nodeID || person.personID" :photoSrc="person.photoSrc"
                                :name="person.firstname + ' ' + person.lastname"
                                :years="formatEventDescription(eventType, person.eventDate, person.eventYear)" :gender="person.sex"
                                :href="localePath(`/person/${person.personID}`)" :personID="person.personID"
                                :firstname="person.firstname" :lastname="person.lastname" :living="person.living"
                                :placeholder="person.placeholder" :isSmall="true" :class="{ 'PersonCard_small_guest': isPrivatePerson(person) }" />
                        </div>
                    </div>
                </template>

                <div v-if="subPlacesLoading || hasSubPlaces" class="place-events-panel__subplaces">
                    <h3 class="place-events-panel__event-type">{{ $t('placeEventsPanel.subLocations') }}</h3>
                    <div class="place-events-panel__subplace-list">
                        <div v-if="subPlacesLoading && !hasSubPlaces"
                            class="place-events-panel__subplace-loading" role="status"
                            :aria-label="$t('placeEventsPanel.loadingSubLocations')">
                            <span class="place-events-panel__inline-spinner" aria-hidden="true"></span>
                        </div>
                        <button v-for="subPlace in sortedSubPlaces" :key="subPlace.ID" type="button"
                            class="place-events-panel__subplace" @click="$emit('select-sub-place', subPlace)">
                            <span class="place-events-panel__subplace-name">{{ subPlace.place }}</span>
                            <span v-if="subPlacesLoading"
                                class="place-events-panel__subplace-count place-events-panel__subplace-count--loading"
                                role="status">
                                <span class="place-events-panel__inline-spinner" aria-hidden="true"></span>
                            </span>
                            <span v-else class="place-events-panel__subplace-count">
                                {{ $t('placeEventsPanel.eventCount', { count: subPlace.totalEvents || 0 }) }}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="!loading && !hasPanelContent" class="place-events-panel__empty">
                <p>{{ $t('placeEventsPanel.noEvents') }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import { fetchPlaceEvents } from '@/api/apiService';
import PersonCard from '@/atoms/PersonCard.vue';
import LoadingSpinner from '@/atoms/LoadingSpinner.vue';
import { getDefaultPhoto } from '@/utils/photoUtils';
import { getPhotoPath } from '@/utils/mediaUtils';
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import axios from 'axios';
import { isPrivateLivingPerson } from '@/utils/personPrivacy';
import { extractEventYear } from '@/utils/eventDateUtils.mjs';

export default {
    name: 'PlaceEventsPanel',
    components: {
        PersonCard,
        LoadingSpinner,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        placeId: {
            type: [String, Number],
            default: null,
        },
        placeIds: {
            type: Array,
            default: () => [],
        },
        placeName: {
            type: String,
            default: '',
        },
        counts: {
            type: Object,
            default: null,
        },
        countsLoading: {
            type: Boolean,
            default: false,
        },
        subPlaces: {
            type: Array,
            default: () => [],
        },
        subPlacesLoading: {
            type: Boolean,
            default: false,
        },
        inline: {
            type: Boolean,
            default: false,
        },
    },
    setup() {
        const { authToken } = useAuth();
        const { localePath } = useLocale();
        return { authToken, localePath };
    },
    data() {
        return {
            events: [],
            groupedEvents: {},
            loading: false,
            error: null,
            fetchRequestId: 0,
            activeFetchKey: '',
        };
    },
    computed: {
        localizedEventTypeLabels() {
            return {
                birth: this.$t('placeEventsPanel.eventTypes.birth'),
                death: this.$t('placeEventsPanel.eventTypes.death'),
                baptism: this.$t('placeEventsPanel.eventTypes.baptism'),
                burial: this.$t('placeEventsPanel.eventTypes.burial'),
                BURI: this.$t('placeEventsPanel.eventTypes.burial'),
                marriage: this.$t('placeEventsPanel.eventTypes.marriage'),
                OCCU: this.$t('placeEventsPanel.eventTypes.OCCU'),
                EDUC: this.$t('placeEventsPanel.eventTypes.EDUC'),
                education: this.$t('placeEventsPanel.eventTypes.EDUC'),
                ADDR: this.$t('placeEventsPanel.eventTypes.ADDR'),
                RESI: this.$t('placeEventsPanel.eventTypes.RESI'),
                address: this.$t('placeEventsPanel.eventTypes.address'),
                ALIA: this.$t('placeEventsPanel.eventTypes.ALIA'),
                IMMI: this.$t('placeEventsPanel.eventTypes.IMMI'),
                CHRA: this.$t('placeEventsPanel.eventTypes.CHRA'),
                NATU: this.$t('placeEventsPanel.eventTypes.NATU'),
            };
        },
        hasEvents() {
            return this.events.length > 0;
        },
        eventPlaceIds() {
            const ids = this.placeIds.length ? this.placeIds : [this.placeId];
            return Array.from(new Set(
                ids.map((id) => String(id || '').trim()).filter(Boolean)
            ));
        },
        eventPlaceIdsKey() {
            return this.eventPlaceIds.join('|');
        },
        isGroupedPlace() {
            return this.eventPlaceIds.length > 1;
        },
        hasSubPlaces() {
            return this.subPlaces.length > 0;
        },
        hasPanelContent() {
            return this.hasEvents || this.hasSubPlaces || this.countsLoading || this.subPlacesLoading;
        },
        sortedSubPlaces() {
            return [...this.subPlaces].sort((a, b) => {
                const countDiff = Number(b.totalEvents || 0) - Number(a.totalEvents || 0);
                if (countDiff !== 0) return countDiff;
                return String(a.place || '').localeCompare(String(b.place || ''));
            });
        },
        sortedCounts() {
            if (!this.counts) return {};

            const preferredOrder = ['birth', 'death', 'burial', 'baptism', 'education', 'marriage', 'address'];
            const sorted = {};

            // First add items in preferred order
            preferredOrder.forEach(key => {
                if (this.counts[key] && this.counts[key] > 0) {
                    sorted[key] = this.counts[key];
                }
            });

            // Then add remaining items alphabetically
            Object.keys(this.counts)
                .filter(key => !preferredOrder.includes(key) && this.counts[key] > 0)
                .sort()
                .forEach(key => {
                    sorted[key] = this.counts[key];
                });

            return sorted;
        },
        groupedEventsByPlace() {
            const sectionMap = new Map();

            this.events.forEach((event) => {
                const key = String(event.sourcePlaceId || event.eventPlace || '').trim() || 'unknown';
                if (!sectionMap.has(key)) {
                    sectionMap.set(key, {
                        key,
                        sourcePlaceId: event.sourcePlaceId,
                        placeName: event.eventPlace || this.$t('unknownLocation'),
                        events: [],
                    });
                }

                const section = sectionMap.get(key);
                if (!section.placeName && event.eventPlace) {
                    section.placeName = event.eventPlace;
                }
                section.events.push(event);
            });

            return Array.from(sectionMap.values())
                .map((section) => ({
                    ...section,
                    events: this.sortEventsChronologically(section.events),
                    firstEventTimestamp: this.getFirstEventTimestamp(section.events),
                }))
                .sort((a, b) => {
                    if (a.firstEventTimestamp !== null && b.firstEventTimestamp !== null &&
                        a.firstEventTimestamp !== b.firstEventTimestamp) {
                        return a.firstEventTimestamp - b.firstEventTimestamp;
                    }

                    if (a.firstEventTimestamp !== null && b.firstEventTimestamp === null) return -1;
                    if (a.firstEventTimestamp === null && b.firstEventTimestamp !== null) return 1;

                    return String(a.placeName || '').localeCompare(String(b.placeName || ''));
                });
        },
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                if (this.eventPlaceIds.length) {
                    this.fetchEvents();
                }
                // Add ESC key listener when panel opens
                document.addEventListener('keydown', this.handleEscKey);
            } else {
                this.fetchRequestId += 1;
                this.activeFetchKey = '';
                this.loading = false;
                // Remove ESC key listener when panel closes
                document.removeEventListener('keydown', this.handleEscKey);
            }
        },
        eventPlaceIdsKey() {
            if (this.visible && this.eventPlaceIds.length) {
                this.fetchEvents();
            }
        },
    },
    methods: {
        isPrivatePerson(person) {
            return isPrivateLivingPerson(person, this.authToken);
        },
        sortEventsChronologically(events) {
            return [...events].sort((a, b) => {
                const aTimestamp = this.parseEventDateTimestamp(a.eventDate, a.eventYear);
                const bTimestamp = this.parseEventDateTimestamp(b.eventDate, b.eventYear);

                if (aTimestamp !== null && bTimestamp !== null && aTimestamp !== bTimestamp) {
                    return aTimestamp - bTimestamp;
                }

                if (aTimestamp !== null && bTimestamp === null) return -1;
                if (aTimestamp === null && bTimestamp !== null) return 1;

                return String(a.eventType || '').localeCompare(String(b.eventType || '')) ||
                    String(a.lastname || '').localeCompare(String(b.lastname || '')) ||
                    String(a.firstname || '').localeCompare(String(b.firstname || ''));
            });
        },
        getFirstEventTimestamp(events) {
            const timestamps = (events || [])
                .map((event) => this.parseEventDateTimestamp(event.eventDate, event.eventYear))
                .filter((timestamp) => timestamp !== null)
                .sort((a, b) => a - b);

            return timestamps.length ? timestamps[0] : null;
        },
        async fetchEvents() {
            const placeIds = this.eventPlaceIds;
            if (!placeIds.length) return;

            const fetchKey = placeIds.join('|');
            if (this.loading && this.activeFetchKey === fetchKey) return;

            const requestId = this.fetchRequestId + 1;
            this.fetchRequestId = requestId;
            this.activeFetchKey = fetchKey;

            this.loading = true;
            this.error = null;
            this.events = [];
            this.groupedEvents = {};

            try {
                const eventLists = await Promise.all(placeIds.map(async (placeId) => {
                    const events = await fetchPlaceEvents(placeId);
                    return (events || []).map((event) => ({
                        ...event,
                        sourcePlaceId: placeId,
                    }));
                }));
                const events = eventLists.flat();
                const photoCache = {};

                // Fetch and augment photo data for each event
                const augmentedEvents = await Promise.all(
                    events.map(async (event) => {
                        let photoPath = '';
                        try {
                            if (event.placeholder || !event.personID) {
                                return { ...event, photoSrc: '' };
                            }
                            if (!photoCache[event.personID]) {
                                photoCache[event.personID] = axios.get(
                                    `${process.env.VUE_APP_BACKEND_API_URL}/person-default-photo/${event.personID}`,
                                    {
                                        headers: {
                                            'X-API-KEY': process.env.VUE_APP_API_KEY,
                                        },
                                    }
                                );
                            }

                            const photoResponse = await photoCache[event.personID];
                            photoPath = photoResponse.data
                                ? getPhotoPath(photoResponse.data.path)
                                : '';
                        } catch (error) {
                            console.error(`Error fetching profile image for personID ${event.personID}:`, error);
                        }

                        // Determine gender for fallback photo
                        const gender = event.sex === 'M' ? 'M' : event.sex === 'F' ? 'F' : null;

                        // Augment the event with photoSrc and fallback logic
                        return {
                            ...event,
                            photoSrc: photoPath || getDefaultPhoto(gender),
                        };
                    })
                );

                if (this.fetchRequestId !== requestId || this.eventPlaceIdsKey !== fetchKey) return;

                this.events = augmentedEvents;

                // Group events by type
                this.groupedEvents = this.events.reduce((groups, event) => {
                    if (!groups[event.eventType]) groups[event.eventType] = [];
                    groups[event.eventType].push(event);
                    return groups;
                }, {});
            } catch (err) {
                if (this.fetchRequestId !== requestId) return;
                console.error('Error fetching events:', err);
                this.error = this.$t('placeEventsPanel.loadError');
            } finally {
                if (this.fetchRequestId === requestId) {
                    this.loading = false;
                    this.activeFetchKey = '';
                }
            }
        },
        parseEventDateTimestamp(dateString, eventYear = null) {
            const year = extractEventYear(dateString, eventYear);
            if (year === null) return null;

            if (typeof dateString !== 'string') {
                return (year * 10000) + 101;
            }

            const cleaned = dateString
                .replace(/^(?:[~±≈]|circa|ca\.?|c\.?|abt\.?|about|bef\.?|before|aft\.?|after)\s*/i, '')
                .replace(/\?/g, '')
                .trim();
            if (!cleaned) return (year * 10000) + 101;

            const month = Number(cleaned.match(/^\d{1,4}-(\d{1,2})/)?.[1] || 1);
            const day = Number(cleaned.match(/^\d{1,4}-\d{1,2}-(\d{1,2})/)?.[1] || 1);

            return (year * 10000) + (Math.max(month, 1) * 100) + Math.max(day, 1);
        },
        getGroupedPlaceEventKey(event) {
            return [
                event.sourcePlaceId,
                event.personID,
                event.eventType,
                event.eventDate,
                event.eventYear,
                event.eventPlace,
            ].join('-');
        },
        formatEventDescription(eventType, eventDate, eventYear = null) {
            const year = extractEventYear(eventDate, eventYear);
            if (year === null) return this.$t('placeEventsPanel.dateUnknown');
            const normalizedEventType = eventType === 'BURI' ? 'burial' : eventType;
            const key = `placeEventsPanel.eventDescriptions.${normalizedEventType}`;
            const translated = this.$t(key, { year });
            if (translated === key) {
                return this.$t('placeEventsPanel.eventDescriptions.other', { year });
            }
            return translated;
        },
        handleClose() {
            this.$emit('close');
        },
        handleEscKey(event) {
            if (event.key === 'Escape' && this.visible) {
                this.handleClose();
            }
        },
    },
    beforeUnmount() {
        // Clean up ESC key listener when component is destroyed
        document.removeEventListener('keydown', this.handleEscKey);
    },
};
</script>

<style scoped>
.place-events-panel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1000;
    pointer-events: none;
    transition: opacity 0.3s ease;
    opacity: 0;
}

/* When embedded inside a relative container (e.g. homepage globe) */
.place-events-panel--inline {
    position: absolute;
}

.place-events-panel--visible {
    pointer-events: all;
    opacity: 1;
}

.place-events-panel__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    transition: opacity 0.3s ease;
    opacity: 0;
}

.place-events-panel--visible .place-events-panel__overlay {
    opacity: 1;
}

.place-events-panel__content {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 500px;
    max-width: 90vw;
    background: white;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    transform: translateX(100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.place-events-panel--visible .place-events-panel__content {
    transform: translateX(0);
}

.place-events-panel__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 32px 32px 24px;
    border-bottom: 1px solid #E5E5E5;
    gap: 16px;
}

.place-events-panel__title-wrapper {
    flex: 1;
}

.place-events-panel__title {
    font-size: 24px;
    font-weight: 600;
    color: var(--brand-text-dark);
    margin: 0;
    line-height: 1.3;
}

.place-events-panel__subtitle {
    font-size: 14px;
    color: #666;
    margin: 8px 0 0;
}

/* Event counts display */
.place-events-panel__counts {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
}

.place-events-panel__count-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    padding: 4px 12px;
    background: #F5F5F5;
    border-radius: var(--border-radius-lg);
    white-space: nowrap;
}

.place-events-panel__count-item .count-label {
    color: #666;
}

.place-events-panel__count-item .count-value {
    font-weight: 600;
    color: var(--brand-text-dark);
}

.place-events-panel__count-item--loading {
    min-width: 48px;
    justify-content: center;
}

.place-events-panel__inline-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(198, 40, 40, 0.18);
    border-top-color: #c62828;
    border-radius: 50%;
    display: inline-block;
    animation: place-events-panel-spin 0.8s linear infinite;
}

@keyframes place-events-panel-spin {
    to {
        transform: rotate(360deg);
    }
}

/* External close button (absolute positioned left of panel) */
.place-events-panel__close-external {
    position: absolute;
    top: 32px;
    right: calc(500px + 12px);
    width: 40px;
    height: 40px;
    padding: 10px;
    cursor: pointer;
    background-color: #fff;
    border: none;
    border-radius: var(--border-radius-md);
    transition: var(--transition-ease);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1001;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--brand-text-dark);
}

.place-events-panel__close-external:hover {
    background-color: #f0f0f0;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
}

.place-events-panel__close {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--border-radius-md);
    color: var(--brand-text-dark);
    transition: var(--transition-ease);
}

.place-events-panel__close:hover {
    background: #F5F5F5;
}

.place-events-panel__body {
    flex: 1;
    overflow-y: auto;
    padding: 24px 32px 32px;
    /* background-color: var(--brand-bg-2); */
}

.place-events-panel__loading,
.place-events-panel__error,
.place-events-panel__empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
    text-align: center;
    color: #666;
}

.place-events-panel__error {
    color: #d32f2f;
}

.place-events-panel__event-group {
    margin-bottom: 32px;
}

.place-events-panel__event-group:last-child {
    margin-bottom: 0;
}

.place-events-panel__event-type {
    font-size: 18px;
    font-weight: 600;
    color: var(--brand-text-dark);
    margin: 0 0 16px;
}

.place-events-panel__person-cards {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.place-events-panel__subplaces {
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid #E5E5E5;
}

.place-events-panel__subplace-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.place-events-panel__subplace {
    width: 100%;
    border: 1px solid #E5E5E5;
    background: #fff;
    border-radius: var(--border-radius-md);
    padding: 12px 14px;
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 12px;
    text-align: left;
    cursor: pointer;
    transition: var(--transition-ease);
}

.place-events-panel__subplace:hover {
    border-color: #c62828;
    background: #fff7f7;
}

.place-events-panel__subplace-name {
    color: var(--brand-text-dark);
    font-weight: 600;
    line-height: 1.35;
}

.place-events-panel__subplace-count {
    color: #666;
    font-size: 13px;
    white-space: nowrap;
}

.place-events-panel__subplace-count--loading {
    min-width: 74px;
    display: flex;
    justify-content: flex-end;
}

.place-events-panel__subplace-loading {
    min-height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #E5E5E5;
    border-radius: var(--border-radius-md);
    background: #fff;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .place-events-panel__content {
        width: 100%;
        max-width: 100vw;
    }

    .place-events-panel__header {
        padding: 24px 20px 20px;
    }

    .place-events-panel__title {
        font-size: 20px;
    }

    .place-events-panel__body {
        padding: 20px;
    }

    .place-events-panel__close-external {
        right: calc(100% - 48px);
        top: 16px;
    }

    .place-events-panel__subplace {
        grid-template-columns: 1fr;
        gap: 4px;
    }
}

/* Smooth scrolling */
.place-events-panel__body {
    scrollbar-width: thin;
    scrollbar-color: #C0C0C0 transparent;
}

.place-events-panel__body::-webkit-scrollbar {
    width: 8px;
}

.place-events-panel__body::-webkit-scrollbar-track {
    background: transparent;
}

.place-events-panel__body::-webkit-scrollbar-thumb {
    background-color: #C0C0C0;
    border-radius: 4px;
}

.place-events-panel__body::-webkit-scrollbar-thumb:hover {
    background-color: #A0A0A0;
}
</style>
